/**
 * The 32 mm SYSTEM (ADR 0061, owner 2026-08-09: "We use the 32MM system so we
 * should stick with that").
 *
 * ## The owner's geometry, verbatim
 *
 *   - a system hole is 5 mm across;
 *   - the CENTRE of the FIRST hole is **9.5 mm above the BOTTOM OF THE PANEL** —
 *     the panel's own bottom edge, not the floor and not the interior band;
 *   - hole centres repeat every **32 mm** up the panel;
 *   - "panel heights should stop at spacing where you have the top hole plus
 *     9.5 mm", i.e. a legal panel ends 9.5 mm above its last hole centre.
 *
 * Which gives, with holes numbered from 1:
 *
 *   holeCentreMm(n)      = 9.5 + 32·(n − 1) = 32n − 22.5
 *   legalPanelHeightMm(n) = holeCentreMm(n) + 9.5 = 32n − 13
 *
 * ## Metric is the record
 *
 * The shop's system is metric even though this app's UI is imperial, so the
 * RULES are millimetres (`system_hole_pitch_mm`, `system_hole_first_offset_mm`)
 * and every inch figure is converted at the point of use. Carrying inches as
 * the record would accumulate rounding up a 76-hole panel.
 *
 * ## Independent confirmation
 *
 * The CV elevation exports this repo's price catalog was built from show tall
 * panels at 83.90" and 95.24". Those are exactly legal System-32 panel heights:
 * 2131 mm (hole 67) = 83.898" and 2419 mm (hole 76) = 95.236". CV's real panel
 * heights already obey this rule, and the "84 inch" / "95 1/4 inch" figures the
 * app has always used are the rounded LABELS for them (ADR 0011).
 *
 * ## How a shelf meets a hole
 *
 *   - an ADJUSTABLE shelf rests on PINS, so its BOTTOM face sits at the hole
 *     centre;
 *   - a FIXED shelf is anchored with rafix bolts, so it is VERTICALLY CENTRED
 *     on the hole centre.
 *
 * The consequence the owner stated — a fixed 3/4" shelf sits 3/8" lower than an
 * adjustable shelf at the same hole — is therefore not a separate placement
 * rule. It FALLS OUT from the one datum conversion below. Shelf thickness
 * changes the board's span around that datum; it never changes which system
 * hole supplies the datum.
 *
 * Pure — no DOM, no state — so all of it is node-testable beside the solver.
 */

/** Millimetres per inch, so the metric figures can be stated as what they are. */
export const MM_PER_INCH = 25.4;

/** The owner's two figures, in millimetres — the RULE DEFAULTS' source. */
export const SYSTEM_HOLE_PITCH_MM = 32;
export const SYSTEM_HOLE_FIRST_OFFSET_MM = 9.5;
/** The hole itself. Not used by the geometry; recorded because the shop states it. */
export const SYSTEM_HOLE_DIAMETER_MM = 5;

/** 32 mm expressed in the app's units, for callers that want the pitch inline. */
export const SYSTEM_HOLE_PITCH_UNITS = SYSTEM_HOLE_PITCH_MM / MM_PER_INCH;

/** Beyond ~2000 holes something upstream is wrong; a guard, not a limit. */
const MAX_HOLES = 2000;
const EPS = 1e-9;

const isPos = (v) => Number.isFinite(v) && v > 0;

/** Resolved millimetre figures — one place the fallbacks live. */
function mmOpts(opts = {}) {
  return {
    pitchMm: isPos(opts.pitchMm) ? opts.pitchMm : SYSTEM_HOLE_PITCH_MM,
    firstOffsetMm: Number.isFinite(opts.firstOffsetMm) && opts.firstOffsetMm >= 0
      ? opts.firstOffsetMm : SYSTEM_HOLE_FIRST_OFFSET_MM,
  };
}

/** mm → the app's units (inches). */
export function mmToUnits(mm) {
  return mm / MM_PER_INCH;
}

/**
 * The centre of hole `n` (n starting at 1), in MILLIMETRES above the panel's
 * bottom edge: `32n − 22.5` with the shop's figures.
 */
export function holeCentreMm(n, opts = {}) {
  const { pitchMm, firstOffsetMm } = mmOpts(opts);
  return firstOffsetMm + pitchMm * (Math.max(1, Math.round(n)) - 1);
}

/**
 * The centre of hole `n` as a height above the PANEL BOTTOM in the app's units.
 * The datum is load-bearing — see `systemHoleRows` for the conversion into the
 * floor-referenced elevations the rest of the app speaks.
 */
export function holeCentreFromPanelBottom(n, opts = {}) {
  return mmToUnits(holeCentreMm(n, opts));
}

/**
 * The hole nearest a height ABOVE THE PANEL BOTTOM (same datum as
 * holeCentreFromPanelBottom). Never returns a hole below the first one.
 *
 * @returns {{n:number, atUnits:number, atMm:number}}
 */
export function nearestHole(heightAbovePanelBottomUnits, opts = {}) {
  const { pitchMm, firstOffsetMm } = mmOpts(opts);
  const mm = (Number.isFinite(heightAbovePanelBottomUnits) ? heightAbovePanelBottomUnits : 0) * MM_PER_INCH;
  const n = Math.max(1, Math.round((mm - firstOffsetMm) / pitchMm) + 1);
  const atMm = holeCentreMm(n, opts);
  return { n, atMm, atUnits: mmToUnits(atMm) };
}

/**
 * A LEGAL panel height (owner: panels "stop at spacing where you have the top
 * hole plus 9.5 mm") — `32n − 13` mm for a panel whose last hole is `n`.
 */
export function legalPanelHeightMm(n, opts = {}) {
  const { firstOffsetMm } = mmOpts(opts);
  return holeCentreMm(n, opts) + firstOffsetMm;
}

/** The same figure in the app's units. */
export function legalPanelHeight(n, opts = {}) {
  return mmToUnits(legalPanelHeightMm(n, opts));
}

/**
 * The legal panel height nearest a stated one, and how far it moved.
 *
 * Deliberately a QUERY, not a mutation: nothing calls this on load. Existing
 * sketches keep the heights they were saved with (the app's long-standing
 * 95.25" is 0.014" off the true 95.236", which is not worth rewriting anyone's
 * job over) — the snap is offered where a height is EDITED (ADR 0061).
 *
 * @returns {{n:number, heightUnits:number, deltaUnits:number}|null}
 */
export function nearestLegalPanelHeight(heightUnits, opts = {}) {
  if (!isPos(heightUnits)) return null;
  const { pitchMm, firstOffsetMm } = mmOpts(opts);
  const mm = heightUnits * MM_PER_INCH;
  // legalPanelHeightMm(n) = 2·firstOffset + pitch·(n−1)  →  invert for n.
  const n = Math.max(1, Math.round((mm - 2 * firstOffsetMm) / pitchMm) + 1);
  const snapped = mmToUnits(legalPanelHeightMm(n, opts));
  return { n, heightUnits: snapped, deltaUnits: snapped - heightUnits };
}

/**
 * Half a shelf thickness — the amount a FIXED shelf sits LOWER than an
 * ADJUSTABLE one at the same hole (3/8" on the standard ¾" board).
 *
 * Exported rather than inlined so the relationship has exactly one definition
 * and the test asserts the RULE, not the number.
 */
export function fixedShelfDropUnits(thicknessUnits) {
  return (isPos(thicknessUnits) ? thicknessUnits : 0.75) / 2;
}

/**
 * The exact drop from a covering FIXED shelf's HOLE to the top of the rod it
 * supports (owner, 2026-08-09):
 *
 * > "For hanging rod placement, they sit between two system holes. The fixed
 * > shelf would be 2 system holes above the top of the rod (so there is one
 * > visible hole between the rod and the fixed shelf, which covers the system
 * > hole). So for rods below the top shelf, they would be between the 3rd and
 * > 4th system hole from the top of the panel."
 *
 * So the relationship is a HOLE COUNT, not a distance: `holeOffset` pitches.
 * At the shop's 32 mm that is 64 mm = 2.5196850", which is what the owner's
 * earlier "~2.5 inches" (ADR 0060 item 4) turns out to have been all along —
 * the same kind of independent confirmation as CV's 83.90"/95.24" panels.
 *
 * @param {number} [holeOffset=2] how many pitches down from the shelf's hole
 * @returns {number} the drop, in inches, derived from the pitch rule
 */
export function rodHoleDropUnits(holeOffset = 2, opts = {}) {
  const { pitchMm } = mmOpts(opts);
  const holes = Number.isFinite(holeOffset) && holeOffset > 0 ? holeOffset : 2;
  return mmToUnits(pitchMm * holes);
}

/**
 * The inter-hole gap a ROD hangs in — the rod datum, deliberately named and
 * tested apart from the SHELF datums (`shelfSpanAtHole`, `shelfHoleReference`).
 *
 *   a shelf is held ON a hole   — adjustable resting on it, fixed centred on it;
 *   a rod hangs BETWEEN holes   — its top AT the hole, its body in the gap
 *                                 below, so it covers that one hole and leaves
 *                                 the next one up visible.
 *
 * @param {number} holeUnits the hole elevation the rod's TOP sits at
 * @param {{elevations:Array<number>}} rows the ladder
 * @returns {{fromUnits:number, toUnits:number, atUnits:number}} the gap between
 *   that hole and the next one DOWN (`fromUnits` falls back to one pitch below
 *   when the ladder ends there — the gap is real even if the hole is not bored)
 */
export function rodSpanBetweenHoles(holeUnits, rows = {}) {
  const list = rows?.elevations ?? [];
  const { pitchMm } = mmOpts({ pitchMm: rows.pitchMm });
  let below = holeUnits - mmToUnits(pitchMm);
  for (let i = list.length - 1; i >= 0; i -= 1) {
    if (list[i] < holeUnits - EPS) { below = list[i]; break; }
  }
  return { fromUnits: below, toUnits: holeUnits, atUnits: holeUnits };
}

/**
 * The hole ladder for one carcass band, IN FLOOR-REFERENCED ELEVATIONS.
 *
 * ## The datum conversion — the one place it happens
 *
 * The owner's geometry is measured from the PANEL'S BOTTOM EDGE. Everything
 * else in this app (the solver, the elevation, every pin a designer types) is
 * measured from the FINISHED FLOOR. So:
 *
 *     elevation(n) = panelBottomUnits + holeCentreFromPanelBottom(n)
 *
 * `panelBottomUnits` is the placed unit's own `bottomUnits` — which already
 * carries the platform, the stack and the to-height solve. It is NOT the band
 * bottom: the band starts at the TOP FACE of the structural bottom shelf, a
 * couple of inches higher, and measuring the ladder from there would shift
 * every hole in the job. A caller that genuinely has no panel (a bare band in a
 * test) falls back to the band bottom, and says so by passing nothing.
 *
 * @param {{bottomUnits:number, topUnits:number}} band the clear interior band
 * @param {object} [opts]
 * @param {number} [opts.panelBottomUnits] the panel's bottom edge, off the floor
 * @param {number} [opts.pitchMm] `system_hole_pitch_mm` (32)
 * @param {number} [opts.firstOffsetMm] `system_hole_first_offset_mm` (9.5)
 * @returns {{
 *   pitchMm:number, firstOffsetMm:number, panelBottomUnits:number,
 *   elevations:Array<number>,  // hole CENTRES off the floor, ascending
 *   numbers:Array<number>,     // the owner's hole number n for each elevation
 * }}
 */
export function systemHoleRows(band = {}, opts = {}) {
  const { pitchMm, firstOffsetMm } = mmOpts(opts);
  const bottom = Number.isFinite(band.bottomUnits) ? band.bottomUnits : 0;
  const top = Number.isFinite(band.topUnits) ? Math.max(bottom, band.topUnits) : bottom;
  const panelBottomUnits = Number.isFinite(opts.panelBottomUnits) ? opts.panelBottomUnits : bottom;

  const elevations = [];
  const numbers = [];
  // Start at the first hole at or above the band bottom rather than walking the
  // whole panel: a stacked unit's band can begin a metre up its own panel.
  const startN = Math.max(1, Math.ceil(((bottom - panelBottomUnits) * MM_PER_INCH - firstOffsetMm) / pitchMm) + 1);
  for (let k = 0; k < MAX_HOLES; k += 1) {
    const n = startN + k;
    const at = panelBottomUnits + mmToUnits(holeCentreMm(n, { pitchMm, firstOffsetMm }));
    if (at > top + EPS) break;
    if (at < bottom - EPS) continue;
    elevations.push(at);
    numbers.push(n);
  }
  return { pitchMm, firstOffsetMm, panelBottomUnits, elevations, numbers };
}

/** Index of the hole nearest `elevationUnits`, or -1 when the ladder is empty. */
export function nearestHoleIndex(elevationUnits, rows) {
  const list = rows?.elevations ?? [];
  if (!list.length || !Number.isFinite(elevationUnits)) return -1;
  let best = 0;
  let bestGap = Math.abs(list[0] - elevationUnits);
  for (let i = 1; i < list.length; i += 1) {
    const gap = Math.abs(list[i] - elevationUnits);
    if (gap < bestGap - EPS) { best = i; bestGap = gap; }
  }
  return best;
}

/**
 * The vertical span a shelf occupies when held at hole `holeUnits`.
 *
 *   fixed      → centred on the hole      (rafix bolts through the panel)
 *   adjustable → bottom face at the hole  (resting on pins)
 *
 * @returns {{fromUnits:number, toUnits:number, holeUnits:number}}
 *   `toUnits` is the shelf's TOP SURFACE — the figure a designer pins with.
 */
export function shelfSpanAtHole(holeUnits, { fixed = false, thicknessUnits = 0.75 } = {}) {
  const t = isPos(thicknessUnits) ? thicknessUnits : 0.75;
  return fixed
    ? { fromUnits: holeUnits - t / 2, toUnits: holeUnits + t / 2, holeUnits }
    : { fromUnits: holeUnits, toUnits: holeUnits + t, holeUnits };
}

/** The face of a shelf that MUST land on the hole: its centre when fixed, its
 *  bottom when adjustable. The inverse of shelfSpanAtHole. */
export function shelfHoleReference(span = {}, { fixed = false } = {}) {
  const from = Number.isFinite(span.fromUnits) ? span.fromUnits : 0;
  const to = Number.isFinite(span.toUnits) ? span.toUnits : from;
  return fixed ? (from + to) / 2 : from;
}

/**
 * Snaps ONE shelf to the nearest legal hole, keeping it inside the band.
 *
 * A hole whose shelf would poke out of the band is not legal, so the search
 * walks to the nearest hole that fits rather than drawing a board through the
 * carcass. Returns null when NO hole can hold the shelf (a band shorter than
 * one shelf, or an empty ladder) — the caller then leaves the solved span
 * alone, which is the "never silently move it" policy the pins already use.
 *
 * @returns {{fromUnits:number, toUnits:number, holeUnits:number, holeIndex:number}|null}
 */
export function snapShelfToHole(span, rows, opts = {}) {
  const list = rows?.elevations ?? [];
  if (!list.length) return null;
  const { fixed = false, thicknessUnits = 0.75, band = null } = opts;
  const want = nearestHoleIndex(shelfHoleReference(span, { fixed }), rows);
  if (want < 0) return null;
  const fits = (i) => {
    if (!band) return true;
    const s = shelfSpanAtHole(list[i], { fixed, thicknessUnits });
    return s.fromUnits >= band.bottomUnits - EPS && s.toUnits <= band.topUnits + EPS;
  };
  if (fits(want)) return { ...shelfSpanAtHole(list[want], { fixed, thicknessUnits }), holeIndex: want };
  for (let step = 1; step < list.length; step += 1) {
    for (const i of [want - step, want + step]) {
      if (i >= 0 && i < list.length && fits(i)) {
        return { ...shelfSpanAtHole(list[i], { fixed, thicknessUnits }), holeIndex: i };
      }
    }
  }
  return null;
}

/**
 * Snaps a whole ROW of evenly distributed shelves (a shelfGroup) at once,
 * keeping them DISTINCT: two lines that round to the same hole would draw one
 * board where the designer asked for two, so the upper one takes the next hole.
 *
 * The even-distribution INTENT is preserved (each line starts from the position
 * the solver gave it); only the landing is quantized — exactly what the owner
 * described for existing solved shelves.
 *
 * @param {Array<number>} references ascending hole-reference elevations
 * @param {object} [opts]
 * @param {Array<number|null>} [opts.minima] per reference, the LOWEST hole it
 *   may land on. A shelf standing on a FIXED element (a drawer bank, another
 *   board) must round UP rather than to the nearest hole: rounding down would
 *   squeeze the fixed thing underneath, which cannot shrink, and report an
 *   overflow the designer has no way to fix (ADR 0062). Only the landing moves;
 *   the even-distribution intent is still where the walk starts.
 * @returns {Array<number|null>} one entry per reference: the hole elevation it
 *   landed on, or NULL when the ladder ran out before the count asked for — the
 *   caller then keeps that shelf's unsnapped position rather than stacking a
 *   second board on the top hole.
 */
export function snapShelfRowToHoles(references, rows, opts = {}) {
  const list = rows?.elevations ?? [];
  const refs = (Array.isArray(references) ? references : []).filter(Number.isFinite);
  if (!list.length || !refs.length) return refs.slice();
  const minima = Array.isArray(opts.minima) ? opts.minima : [];
  const out = [];
  let floorIdx = -1;
  refs.forEach((ref, k) => {
    let i = nearestHoleIndex(ref, rows);
    const min = minima[k];
    if (Number.isFinite(min)) while (i < list.length && list[i] < min - EPS) i += 1;
    if (i <= floorIdx) i = floorIdx + 1;
    if (i > list.length - 1) {
      out.push(null);
      return;
    }
    floorIdx = i;
    out.push(list[i]);
  });
  return out;
}
